import { defineStore } from 'pinia'
import type { PrestationProduct } from '@/types/prestation'

interface CartItem extends PrestationProduct {
  quantity: number
}

const CART_STORAGE_KEY = 'efashion_cart'

// Fonction pour charger depuis localStorage
const loadCartFromStorage = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    return []
  }
}

// Fonction pour sauvegarder dans localStorage
const saveCartToStorage = (items: CartItem[]) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
  } catch (error) {
    // Erreur silencieuse lors de la sauvegarde
  }
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: loadCartFromStorage()
  }),

  getters: {
    // Nombre total d'articles
    itemCount: (state) => state.items.reduce((sum, item) => sum + item.quantity, 0),
    
    // Total du panier
    total: (state) => state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    
    // Vérifier si le panier est vide
    isEmpty: (state) => state.items.length === 0
  },

  actions: {
    // Ajouter un produit
    addItem(product: PrestationProduct) {
      const existing = this.items.find(item => item.id === product.id)
      
      if (existing) {
        existing.quantity++
      } else {
        this.items.push({ ...product, quantity: 1 })
      }
      
      saveCartToStorage(this.items)
    },

    // Supprimer un produit
    removeItem(productId: number) {
      this.items = this.items.filter(item => item.id !== productId)
      saveCartToStorage(this.items)
    },

    // Modifier la quantité d'un produit
    setQuantity(productId: number, quantity: number) {
      const item = this.items.find(item => item.id === productId)
      
      if (item) {
        if (quantity <= 0) {
          this.removeItem(productId)
        } else {
          item.quantity = quantity
          saveCartToStorage(this.items)
        }
      }
    },

    // Mettre à jour la quantité
    updateQuantity(productId: number, quantity: number) {
      const item = this.items.find(item => item.id === productId)
      
      if (item) {
        if (quantity <= 0) {
          this.removeItem(productId)
        } else {
          item.quantity = quantity
          saveCartToStorage(this.items)
        }
      }
    },

    // Vider le panier
    clearCart() {
      this.items = []
      saveCartToStorage(this.items)
    }
  }
})
